import { api, apiPublic } from './client';
import type { ClassOffering, ClassSession } from '../features/classes/types';
import type { RecurrenceRule } from '../shared/types';
export type { ClassOffering, ClassSession } from '../features/classes/types';

export interface ClassSeriesConfig {
  id: string;
  classOfferingId: string;
  startDate: string; // YYYY-MM-DD
  timeStart: string; // HH:mm
  timeEnd: string; // HH:mm
  endDate?: string | null;
  endedAt?: string | null;
  isActive?: boolean;
  recurrence: RecurrenceRule;
  createdAt: string;
  updatedAt: string;
}

export async function getPublicClasses(): Promise<ClassOffering[]> {
  return api.get<ClassOffering[]>('/api/classes/public');
}

export async function getPublicClassSessions(classOfferingId: string): Promise<ClassSession[]> {
  return api.get<ClassSession[]>(`/api/classes/public/${classOfferingId}/sessions`);
}

export async function getClasses(): Promise<ClassOffering[]> {
  return api.get<ClassOffering[]>('/api/classes');
}

export async function getClass(id: string): Promise<ClassOffering | null> {
  try {
    return await api.get<ClassOffering>(`/api/classes/${id}`);
  } catch {
    return null;
  }
}

export async function getClassSessions(classOfferingId: string): Promise<ClassSession[]> {
  return api.get<ClassSession[]>(`/api/classes/${classOfferingId}/sessions`);
}

export async function getClassSeries(classOfferingId: string): Promise<ClassSeriesConfig | null> {
  return api.get<ClassSeriesConfig | null>(`/api/classes/${classOfferingId}/series`);
}

export async function getPublicClassSeries(classOfferingId: string): Promise<ClassSeriesConfig | null> {
  return apiPublic.get<ClassSeriesConfig | null>(`/api/classes/public/${classOfferingId}/series`);
}

export async function endClassSeries(classOfferingId: string, endDate: string): Promise<{ id: string; endDate: string | null }> {
  return api.post<{ id: string; endDate: string | null }>(`/api/classes/${classOfferingId}/series/end`, { endDate });
}

export async function createClassOffering(data: {
  title: string;
  subtitle?: string | null;
  descriptionHtml?: string | null;
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
  displayOrder?: number;
  isPublic?: boolean;
  imageIds?: string[];
  sessionStartDate?: string | null;
  sessionTimeStart?: string | null;
  sessionTimeEnd?: string | null;
  recurrence?: RecurrenceRule;
}): Promise<ClassOffering> {
  return api.post<ClassOffering>('/api/classes', data);
}

export async function updateClassOffering(
  id: string,
  data: Partial<{
    title: string;
    subtitle: string | null;
    descriptionHtml: string | null;
    dateRangeStart: string | null;
    dateRangeEnd: string | null;
    displayOrder: number;
    isPublic: boolean;
    imageIds: string[];
    sessionStartDate: string | null;
    sessionTimeStart: string | null;
    sessionTimeEnd: string | null;
    recurrence: RecurrenceRule;
  }>
): Promise<ClassOffering> {
  return api.patch<ClassOffering>(`/api/classes/${id}`, data);
}

export async function deleteClassOffering(id: string): Promise<void> {
  await api.delete(`/api/classes/${id}`);
}

export function formatClassDateRange(start: string | null, end: string | null): string {
  if (!start && !end) return '';
  const s = start ? new Date(start).toLocaleDateString() : '';
  const e = end ? new Date(end).toLocaleDateString() : '';
  if (s && e) return `${s} – ${e}`;
  return s || e;
}
